import React from 'react'
import { ChevronDownIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/solid'
import { t } from 'i18next'

// Departure Trip Addons Component
export const DepartureTripAddons = ({
    tripMeals,
    mealsLoading,
    selectedAddons,
    setSelectedAddons,
    addonPrices,
    addonPriceConfig,
    addonConfigLoading,
    passengersDetails,
    openAddonSection,
    setOpenAddonSection,
    getMealQuantity,
    updateMealQuantity,
    tripDetails,
    departureTrip
}) => {
    // Helper functions for passenger validation
    const getTotalPassengers = () => {
        return passengersDetails?.passengers?.length || 0
    }

    const getInfantCount = () => {
        return passengersDetails?.passengers?.filter(p => p.seat_info.age_group_code === 'INF').length || 0
    }

    const getNonInfantCount = () => {
        return passengersDetails?.passengers?.filter(p => p.seat_info.age_group_code !== 'INF').length || 0
    }

    // Helper function to update special item quantity
    const updateSpecialItemQuantity = (itemType, quantity) => {
        setSelectedAddons(prev => ({
            ...prev,
            departure: {
                ...prev.departure,
                special_items: {
                    ...prev.departure.special_items,
                    [itemType]: quantity
                }
            }
        }))
    }

    // Helper function to update shuttle service selection
    const updateShuttleServiceSelection = (selected, shuttleProvider = null) => {
        setSelectedAddons(prev => ({
            ...prev,
            departure: {
                ...prev.departure,
                shuttle_service: {
                    selected: selected,
                    route_shuttle_id: selected ? shuttleProvider?.route_shuttle_id : null,
                    provider_details: selected ? shuttleProvider : null
                }
            }
        }))
    }

    // Helper function to get shuttle providers for departure trip
    const getShuttleProviders = () => {
        return departureTrip?.shuttle_providers || []
    }

    // Helper function to check if shuttle service is available
    const isShuttleServiceAvailable = () => {
        const providers = getShuttleProviders()
        return providers.length > 0 && providers.some(provider => provider.shuttle_provider?.isactive)
    }

    return (
        <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
                {t('Departure Trip')} - {t('Addons & Upgrades')}
            </h3>
        
            {/* Departure Meal/Snack Plans */}
            <div className="mb-8">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'departure-meals' ? null : 'departure-meals')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors min-h-[44px]"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Meal/snack plans')}</h3>
                        <p className="text-sm text-gray-600">{t('Preorder meals for a smoother trip')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'departure-meals' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'departure-meals' && (
                    <div className="space-y-3 mt-3">
                        {mealsLoading ? (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="text-sm text-gray-500 mt-2">{t('Loading meals...')}</p>
                            </div>
                        ) : tripMeals.departure.length > 0 ? (
                            tripMeals.departure.map((meal) => (
                                <div key={`departure-meal-${meal.trip_meal_id}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div className="flex items-center">
                                        <label htmlFor={`departure-meal-${meal.trip_meal_id}`} className="text-sm font-medium text-gray-900">
                                            {meal.description?.en || meal.description?.ar || meal.description || 'Meal'}
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm font-semibold text-gray-900">{meal.totalPrice.toFixed(2)} BHD</span>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => {
                                                    const currentQty = getMealQuantity('departure', meal.trip_meal_id)
                                                    if (currentQty > 0) {
                                                        updateMealQuantity('departure', meal.trip_meal_id, currentQty - 1)
                                                    }
                                                }}
                                                className="w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-[#047481] border-2 border-[#047481] hover:bg-[#035a63] flex items-center justify-center text-white transition-colors"
                                                disabled={getMealQuantity('departure', meal.trip_meal_id) === 0}
                                            >
                                                <MinusIcon className="w-4 h-4" />
                                            </button>
                                            <span className="w-8 text-center text-sm font-medium text-gray-900 bg-white px-2 py-1 rounded border-2 border-gray-300">
                                                {getMealQuantity('departure', meal.trip_meal_id)}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const currentQty = getMealQuantity('departure', meal.trip_meal_id)
                                                    updateMealQuantity('departure', meal.trip_meal_id, currentQty + 1)
                                                }}
                                                className="w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-[#047481] border-2 border-[#047481] hover:bg-[#035a63] flex items-center justify-center text-white transition-colors"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p>{t('No meals available for this trip')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Departure Extra Luggage */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'departure-luggage' ? null : 'departure-luggage')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Extra Luggage')}</h3>
                        <p className="text-sm text-gray-600">{t('Add bags beyond the free allowance')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'departure-luggage' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'departure-luggage' && (
                    <div className="space-y-3 mt-3">
                        {addonConfigLoading ? (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="text-sm text-gray-500 mt-2">{t('Loading luggage options...')}</p>
                            </div>
                        ) : addonPriceConfig.extra_luggage.length > 0 ? (
                            addonPriceConfig.extra_luggage.map((luggage, index) => {
                                const luggageKey = `${luggage.weight_kg}kg`
                                const isSelected = selectedAddons.departure.luggage[luggageKey] || false
                                
                                return (
                                    <div key={`departure-luggage-${luggage.weight_kg}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                name="departure-luggage"
                                                id={`departure-luggage-${luggage.weight_kg}`}
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    const newDeparture = { ...selectedAddons.departure }
                                                    const newLuggage = {}
                                                    // Reset all luggage options
                                                    addonPriceConfig.extra_luggage.forEach(l => {
                                                        newLuggage[`${l.weight_kg}kg`] = false
                                                    })
                                                    // Set selected option
                                                    if (e.target.checked) {
                                                        newLuggage[luggageKey] = true
                                                    }
                                                    newDeparture.luggage = newLuggage
                                                    setSelectedAddons({
                                                        ...selectedAddons,
                                                        departure: newDeparture
                                                    })
                                                }}
                                                className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 focus:ring-teal-500 focus:ring-2"
                                            />
                                            <label htmlFor={`departure-luggage-${luggage.weight_kg}`} className="ml-3 text-sm font-medium text-gray-900">
                                                {luggage.weight_kg}kg Extra Luggage
                                            </label>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-900">{luggage.price} BHD</span>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p className="text-sm">{t('No luggage options available')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Special Items Section */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'departure-special-items' ? null : 'departure-special-items')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Special Items')}</h3>
                        <p className="text-sm text-gray-600">{t('Wheelchair and stroller options')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'departure-special-items' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'departure-special-items' && (
                    <div className="space-y-4 mt-3">
                        {/* Wheelchair */}
                        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900">{t('Wheelchair')}</h4>
                                    <p className="text-xs text-gray-500">{t('Max')}: {getNonInfantCount()} {t('items')}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => {
                                        const currentQty = selectedAddons.departure.special_items.wheelchair || 0
                                        if (currentQty > 0) {
                                            updateSpecialItemQuantity('wheelchair', currentQty - 1)
                                        }
                                    }}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                >
                                    <MinusIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="w-8 text-center text-sm font-medium">
                                    {selectedAddons.departure.special_items.wheelchair || 0}
                                </span>
                                <button
                                    onClick={() => {
                                        const currentQty = selectedAddons.departure.special_items.wheelchair || 0
                                        if (currentQty < getNonInfantCount()) {
                                            updateSpecialItemQuantity('wheelchair', currentQty + 1)
                                        }
                                    }}
                                    disabled={(selectedAddons.departure.special_items.wheelchair || 0) >= getNonInfantCount()}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <PlusIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="text-sm font-semibold text-gray-900 ml-2">
                                    {addonPriceConfig.special_items.wheelchair_price} BHD
                                </span>
                            </div>
                        </div>

                        {/* Stroller - Only show if there are infants */}
                        {getInfantCount() > 0 && (
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900">{t('Stroller')}</h4>
                                        <p className="text-xs text-gray-500">{t('Max')}: {getTotalPassengers()} {t('items')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => {
                                            const currentQty = selectedAddons.departure.special_items.stroller || 0
                                            if (currentQty > 0) {
                                                updateSpecialItemQuantity('stroller', currentQty - 1)
                                            }
                                        }}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                    >
                                        <MinusIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="w-8 text-center text-sm font-medium">
                                        {selectedAddons.departure.special_items.stroller || 0}
                                    </span>
                                    <button
                                        onClick={() => {
                                            const currentQty = selectedAddons.departure.special_items.stroller || 0
                                            if (currentQty < getTotalPassengers()) {
                                                updateSpecialItemQuantity('stroller', currentQty + 1)
                                            }
                                        }}
                                        disabled={(selectedAddons.departure.special_items.stroller || 0) >= getTotalPassengers()}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <PlusIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="text-sm font-semibold text-gray-900 ml-2">
                                        {addonPriceConfig.special_items.stroller_price} BHD
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Shuttle Service Section - Only show if available for international trips */}
            {isShuttleServiceAvailable() && (
                <div className="mb-6">
                    <button
                        onClick={() => setOpenAddonSection(openAddonSection === 'departure-shuttle' ? null : 'departure-shuttle')}
                        className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                        <div className="text-left">
                            <h3 className="text-lg font-semibold text-gray-900">{t('Shuttle Service')}</h3>
                            <p className="text-sm text-gray-600">{t('Bus transport from destination port to drop-off points')}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z"/>
                                </svg>
                            </div>
                            <ChevronDownIcon 
                                className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'departure-shuttle' ? 'rotate-180' : ''}`}
                            />
                        </div>
                    </button>
                    {openAddonSection === 'departure-shuttle' && (
                        <div className="space-y-3 mt-3">
                            {getShuttleProviders().map((shuttleProvider) => {
                                if (!shuttleProvider.shuttle_provider?.isactive) return null;
                                
                                const provider = shuttleProvider.shuttle_provider;
                                const isSelected = selectedAddons.departure.shuttle_service.selected && 
                                                 selectedAddons.departure.shuttle_service.route_shuttle_id === shuttleProvider.route_shuttle_id;
                                
                                return (
                                    <div key={shuttleProvider.route_shuttle_id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start space-x-3">
                                                <input
                                                    type="checkbox"
                                                    id={`departure-shuttle-${shuttleProvider.route_shuttle_id}`}
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        updateShuttleServiceSelection(e.target.checked, e.target.checked ? shuttleProvider : null)
                                                    }}
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <label htmlFor={`departure-shuttle-${shuttleProvider.route_shuttle_id}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                                                        {provider.provider_name}
                                                    </label>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {provider.service_description?.en || provider.service_description?.ar || 'Shuttle service to destination'}
                                                    </p>
                                                    {provider.contact && (
                                                        <div className="text-xs text-gray-500 mt-2">
                                                            <p>{t('Contact')}: {provider.contact.person}</p>
                                                            <p>{t('Phone')}: {provider.contact.mobile_number}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-lg font-semibold text-gray-900">
                                                    {provider.price?.gross || provider.price?.base} BHD
                                                </span>
                                                {provider.price?.tax > 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        {t('Incl. tax')}: {provider.price.tax} BHD
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// Return Trip Addons Component
export const ReturnTripAddons = ({
    tripMeals,
    mealsLoading,
    selectedAddons,
    setSelectedAddons,
    addonPrices,
    addonPriceConfig,
    addonConfigLoading,
    passengersDetails,
    openAddonSection,
    setOpenAddonSection,
    getMealQuantity,
    updateMealQuantity,
    tripDetails
}) => {
    // Helper functions for passenger validation
    const getTotalPassengers = () => {
        return passengersDetails?.passengers?.length || 0
    }

    const getInfantCount = () => {
        return passengersDetails?.passengers?.filter(p => p.seat_info.age_group_code === 'INF').length || 0
    }

    const getNonInfantCount = () => {
        return passengersDetails?.passengers?.filter(p => p.seat_info.age_group_code !== 'INF').length || 0
    }

    // Helper function to update special item quantity
    const updateSpecialItemQuantity = (itemType, quantity) => {
        setSelectedAddons(prev => ({
            ...prev,
            return: {
                ...prev.return,
                special_items: {
                    ...prev.return.special_items,
                    [itemType]: quantity
                }
            }
        }))
    }

    // Helper function to update shuttle service selection for return trip
    const updateShuttleServiceSelection = (selected, shuttleProvider = null) => {
        setSelectedAddons(prev => ({
            ...prev,
            return: {
                ...prev.return,
                shuttle_service: {
                    selected: selected,
                    route_shuttle_id: selected ? shuttleProvider?.route_shuttle_id : null,
                    provider_details: selected ? shuttleProvider : null
                }
            }
        }))
    }

    // Helper function to get shuttle providers for return trip
    const getShuttleProviders = () => {
        return tripDetails?.shuttle_providers || []
    }

    // Helper function to check if shuttle service is available for return trip
    const isShuttleServiceAvailable = () => {
        const providers = getShuttleProviders()
        return providers.length > 0 && providers.some(provider => provider.shuttle_provider?.isactive)
    }

    return (
        <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
                {t('Return Trip')} - {t('Addons & Upgrades')}
            </h3>
            
            {/* Return Meal/Snack Plans */}
            <div className="mb-8">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'return-meals' ? null : 'return-meals')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Meal/snack plans')}</h3>
                        <p className="text-sm text-gray-600">{t('Preorder meals for a smoother trip')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'return-meals' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'return-meals' && (
                    <div className="space-y-3 mt-3">
                        {mealsLoading ? (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="text-sm text-gray-500 mt-2">{t('Loading meals...')}</p>
                            </div>
                        ) : tripMeals.return.length > 0 ? (
                            tripMeals.return.map((meal) => (
                                <div key={`return-meal-${meal.trip_meal_id}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div className="flex items-center">
                                        <label htmlFor={`return-meal-${meal.trip_meal_id}`} className="text-sm font-medium text-gray-900">
                                            {meal.description?.en || meal.description?.ar || meal.description || 'Meal'}
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm font-semibold text-gray-900">{meal.totalPrice.toFixed(2)} BHD</span>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => {
                                                    const currentQty = getMealQuantity('return', meal.trip_meal_id)
                                                    if (currentQty > 0) {
                                                        updateMealQuantity('return', meal.trip_meal_id, currentQty - 1)
                                                    }
                                                }}
                                                className="w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-[#047481] border-2 border-[#047481] hover:bg-[#035a63] flex items-center justify-center text-white transition-colors"
                                                disabled={getMealQuantity('return', meal.trip_meal_id) === 0}
                                            >
                                                <MinusIcon className="w-4 h-4" />
                                            </button>
                                            <span className="w-8 text-center text-sm font-medium text-gray-900 bg-white px-2 py-1 rounded border-2 border-gray-300">
                                                {getMealQuantity('return', meal.trip_meal_id)}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const currentQty = getMealQuantity('return', meal.trip_meal_id)
                                                    updateMealQuantity('return', meal.trip_meal_id, currentQty + 1)
                                                }}
                                                className="w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-[#047481] border-2 border-[#047481] hover:bg-[#035a63] flex items-center justify-center text-white transition-colors"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p>{t('No meals available for this trip')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Return Extra Luggage */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'return-luggage' ? null : 'return-luggage')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Extra Luggage')}</h3>
                        <p className="text-sm text-gray-600">{t('Add bags beyond the free allowance')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'return-luggage' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'return-luggage' && (
                    <div className="space-y-3 mt-3">
                        {addonConfigLoading ? (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="text-sm text-gray-500 mt-2">{t('Loading luggage options...')}</p>
                            </div>
                        ) : addonPriceConfig.extra_luggage.length > 0 ? (
                            addonPriceConfig.extra_luggage.map((luggage, index) => {
                                const luggageKey = `${luggage.weight_kg}kg`
                                const isSelected = selectedAddons.return.luggage[luggageKey] || false
                                
                                return (
                                    <div key={`return-luggage-${luggage.weight_kg}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                name="return-luggage"
                                                id={`return-luggage-${luggage.weight_kg}`}
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    const newReturn = { ...selectedAddons.return }
                                                    const newLuggage = {}
                                                    // Reset all luggage options
                                                    addonPriceConfig.extra_luggage.forEach(l => {
                                                        newLuggage[`${l.weight_kg}kg`] = false
                                                    })
                                                    // Set selected option
                                                    if (e.target.checked) {
                                                        newLuggage[luggageKey] = true
                                                    }
                                                    newReturn.luggage = newLuggage
                                                    setSelectedAddons({
                                                        ...selectedAddons,
                                                        return: newReturn
                                                    })
                                                }}
                                                className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 focus:ring-teal-500 focus:ring-2"
                                            />
                                            <label htmlFor={`return-luggage-${luggage.weight_kg}`} className="ml-3 text-sm font-medium text-gray-900">
                                                {luggage.weight_kg}kg Extra Luggage
                                            </label>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-900">{luggage.price} BHD</span>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p className="text-sm">{t('No luggage options available')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Special Items Section */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'return-special-items' ? null : 'return-special-items')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Special Items')}</h3>
                        <p className="text-sm text-gray-600">{t('Wheelchair and stroller options')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'return-special-items' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'return-special-items' && (
                    <div className="space-y-4 mt-3">
                        {/* Wheelchair */}
                        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900">{t('Wheelchair')}</h4>
                                    <p className="text-xs text-gray-500">{t('Max')}: {getNonInfantCount()} {t('items')}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => {
                                        const currentQty = selectedAddons.return.special_items.wheelchair || 0
                                        if (currentQty > 0) {
                                            updateSpecialItemQuantity('wheelchair', currentQty - 1)
                                        }
                                    }}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                >
                                    <MinusIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="w-8 text-center text-sm font-medium">
                                    {selectedAddons.return.special_items.wheelchair || 0}
                                </span>
                                <button
                                    onClick={() => {
                                        const currentQty = selectedAddons.return.special_items.wheelchair || 0
                                        if (currentQty < getNonInfantCount()) {
                                            updateSpecialItemQuantity('wheelchair', currentQty + 1)
                                        }
                                    }}
                                    disabled={(selectedAddons.return.special_items.wheelchair || 0) >= getNonInfantCount()}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <PlusIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="text-sm font-semibold text-gray-900 ml-2">
                                    {addonPriceConfig.special_items.wheelchair_price} BHD
                                </span>
                            </div>
                        </div>

                        {/* Stroller - Only show if there are infants */}
                        {getInfantCount() > 0 && (
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900">{t('Stroller')}</h4>
                                        <p className="text-xs text-gray-500">{t('Max')}: {getTotalPassengers()} {t('items')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => {
                                            const currentQty = selectedAddons.return.special_items.stroller || 0
                                            if (currentQty > 0) {
                                                updateSpecialItemQuantity('stroller', currentQty - 1)
                                            }
                                        }}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                    >
                                        <MinusIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="w-8 text-center text-sm font-medium">
                                        {selectedAddons.return.special_items.stroller || 0}
                                    </span>
                                    <button
                                        onClick={() => {
                                            const currentQty = selectedAddons.return.special_items.stroller || 0
                                            if (currentQty < getTotalPassengers()) {
                                                updateSpecialItemQuantity('stroller', currentQty + 1)
                                            }
                                        }}
                                        disabled={(selectedAddons.return.special_items.stroller || 0) >= getTotalPassengers()}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <PlusIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="text-sm font-semibold text-gray-900 ml-2">
                                        {addonPriceConfig.special_items.stroller_price} BHD
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Shuttle Service Section - Only show if available for international trips */}
            {isShuttleServiceAvailable() && (
                <div className="mb-6">
                    <button
                        onClick={() => setOpenAddonSection(openAddonSection === 'return-shuttle' ? null : 'return-shuttle')}
                        className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                        <div className="text-left">
                            <h3 className="text-lg font-semibold text-gray-900">{t('Shuttle Service')}</h3>
                            <p className="text-sm text-gray-600">{t('Bus transport from destination port to drop-off points')}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z"/>
                                </svg>
                            </div>
                            <ChevronDownIcon 
                                className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'return-shuttle' ? 'rotate-180' : ''}`}
                            />
                        </div>
                    </button>
                    {openAddonSection === 'return-shuttle' && (
                        <div className="space-y-3 mt-3">
                            {getShuttleProviders().map((shuttleProvider) => {
                                if (!shuttleProvider.shuttle_provider?.isactive) return null;
                                
                                const provider = shuttleProvider.shuttle_provider;
                                const isSelected = selectedAddons.return.shuttle_service.selected && 
                                                 selectedAddons.return.shuttle_service.route_shuttle_id === shuttleProvider.route_shuttle_id;
                                
                                return (
                                    <div key={shuttleProvider.route_shuttle_id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start space-x-3">
                                                <input
                                                    type="checkbox"
                                                    id={`return-shuttle-${shuttleProvider.route_shuttle_id}`}
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        updateShuttleServiceSelection(e.target.checked, e.target.checked ? shuttleProvider : null)
                                                    }}
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <label htmlFor={`return-shuttle-${shuttleProvider.route_shuttle_id}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                                                        {provider.provider_name}
                                                    </label>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {provider.service_description?.en || provider.service_description?.ar || 'Shuttle service to destination'}
                                                    </p>
                                                    {provider.contact && (
                                                        <div className="text-xs text-gray-500 mt-2">
                                                            <p>{t('Contact')}: {provider.contact.person}</p>
                                                            <p>{t('Phone')}: {provider.contact.mobile_number}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-lg font-semibold text-gray-900">
                                                    {provider.price?.gross || provider.price?.base} BHD
                                                </span>
                                                {provider.price?.tax > 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        {t('Incl. tax')}: {provider.price.tax} BHD
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// One-way Trip Addons Component
export const OneWayTripAddons = ({
    tripMeals,
    mealsLoading,
    selectedAddons,
    setSelectedAddons,
    addonPrices,
    addonPriceConfig,
    addonConfigLoading,
    passengersDetails,
    openAddonSection,
    setOpenAddonSection,
    getMealQuantity,
    updateMealQuantity,
    tripDetails
}) => {
    // Helper functions for passenger validation
    const getTotalPassengers = () => {
        return passengersDetails?.passengers?.length || 0
    }

    const getInfantCount = () => {
        return passengersDetails?.passengers?.filter(p => p.seat_info.age_group_code === 'INF').length || 0
    }

    const getNonInfantCount = () => {
        return passengersDetails?.passengers?.filter(p => p.seat_info.age_group_code !== 'INF').length || 0
    }

    // Helper function to update special item quantity
    const updateSpecialItemQuantity = (itemType, quantity) => {
        setSelectedAddons(prev => ({
            ...prev,
            departure: {
                ...prev.departure,
                special_items: {
                    ...prev.departure.special_items,
                    [itemType]: quantity
                }
            }
        }))
    }

    // Helper function to update shuttle service selection for one-way trip
    const updateShuttleServiceSelection = (selected, shuttleProvider = null) => {
        setSelectedAddons(prev => ({
            ...prev,
            departure: {
                ...prev.departure,
                shuttle_service: {
                    selected: selected,
                    route_shuttle_id: selected ? shuttleProvider?.route_shuttle_id : null,
                    provider_details: selected ? shuttleProvider : null
                }
            }
        }))
    }

    // Helper function to get shuttle providers for one-way trip
    const getShuttleProviders = () => {
        return tripDetails?.shuttle_providers || []
    }

    // Helper function to check if shuttle service is available for one-way trip
    const isShuttleServiceAvailable = () => {
        const providers = getShuttleProviders()
        return providers.length > 0 && providers.some(provider => provider.shuttle_provider?.isactive)
    }

    return (
        <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
                {t('One-way Trip')} - {t('Addons & Upgrades')}
            </h3>
            {/* Meal/Snack Plans */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'oneway-meals' ? null : 'oneway-meals')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Meal/snack plans')}</h3>
                        <p className="text-sm text-gray-600">{t('Preorder meals for a smoother trip')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'oneway-meals' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'oneway-meals' && (
                    <div className="space-y-3 mt-3">
                        {mealsLoading ? (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="text-sm text-gray-500 mt-2">{t('Loading meals...')}</p>
                            </div>
                        ) : tripMeals.departure.length > 0 ? (
                            tripMeals.departure.map((meal) => (
                                <div key={`oneway-meal-${meal.trip_meal_id}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div className="flex items-center">
                                        <label htmlFor={`oneway-meal-${meal.trip_meal_id}`} className="text-sm font-medium text-gray-900">
                                            {meal.description?.en || meal.description?.ar || meal.description || 'Meal'}
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm font-semibold text-gray-900">{meal.totalPrice.toFixed(2)} BHD</span>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => {
                                                    const currentQty = getMealQuantity('departure', meal.trip_meal_id)
                                                    if (currentQty > 0) {
                                                        updateMealQuantity('departure', meal.trip_meal_id, currentQty - 1)
                                                    }
                                                }}
                                                className="w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-[#047481] border-2 border-[#047481] hover:bg-[#035a63] flex items-center justify-center text-white transition-colors"
                                                disabled={getMealQuantity('departure', meal.trip_meal_id) === 0}
                                            >
                                                <MinusIcon className="w-4 h-4" />
                                            </button>
                                            <span className="w-8 text-center text-sm font-medium text-gray-900 bg-white px-2 py-1 rounded border-2 border-gray-300">
                                                {getMealQuantity('departure', meal.trip_meal_id)}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const currentQty = getMealQuantity('departure', meal.trip_meal_id)
                                                    updateMealQuantity('departure', meal.trip_meal_id, currentQty + 1)
                                                }}
                                                className="w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-[#047481] border-2 border-[#047481] hover:bg-[#035a63] flex items-center justify-center text-white transition-colors"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p>{t('No meals available for this trip')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Extra Luggage */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'oneway-luggage' ? null : 'oneway-luggage')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Extra Luggage')}</h3>
                        <p className="text-sm text-gray-600">{t('Add bags beyond the free allowance')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'oneway-luggage' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'oneway-luggage' && (
                    <div className="space-y-3 mt-3">
                        {addonConfigLoading ? (
                            <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <p className="text-sm text-gray-500 mt-2">{t('Loading luggage options...')}</p>
                            </div>
                        ) : addonPriceConfig.extra_luggage.length > 0 ? (
                            addonPriceConfig.extra_luggage.map((luggage, index) => {
                                const luggageKey = `${luggage.weight_kg}kg`
                                const isSelected = selectedAddons.departure.luggage[luggageKey] || false
                                
                                return (
                                    <div key={`oneway-luggage-${luggage.weight_kg}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                name="luggage"
                                                id={`luggage-${luggage.weight_kg}`}
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    const newDeparture = { ...selectedAddons.departure }
                                                    const newLuggage = {}
                                                    // Reset all luggage options
                                                    addonPriceConfig.extra_luggage.forEach(l => {
                                                        newLuggage[`${l.weight_kg}kg`] = false
                                                    })
                                                    // Set selected option
                                                    if (e.target.checked) {
                                                        newLuggage[luggageKey] = true
                                                    }
                                                    newDeparture.luggage = newLuggage
                                                    setSelectedAddons({
                                                        ...selectedAddons,
                                                        departure: newDeparture
                                                    })
                                                }}
                                                className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 focus:ring-teal-500 focus:ring-2"
                                            />
                                            <label htmlFor={`luggage-${luggage.weight_kg}`} className="ml-3 text-sm font-medium text-gray-900">
                                                {luggage.weight_kg}kg Extra Luggage
                                            </label>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-900">{luggage.price} BHD</span>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p className="text-sm">{t('No luggage options available')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Special Items Section */}
            <div className="mb-6">
                <button
                    onClick={() => setOpenAddonSection(openAddonSection === 'oneway-special-items' ? null : 'oneway-special-items')}
                    className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">{t('Special Items')}</h3>
                        <p className="text-sm text-gray-600">{t('Wheelchair and stroller options')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'oneway-special-items' ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>
                {openAddonSection === 'oneway-special-items' && (
                    <div className="space-y-4 mt-3">
                        {/* Wheelchair */}
                        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900">{t('Wheelchair')}</h4>
                                    <p className="text-xs text-gray-500">{t('Max')}: {getNonInfantCount()} {t('items')}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => {
                                        const currentQty = selectedAddons.departure.special_items.wheelchair || 0
                                        if (currentQty > 0) {
                                            updateSpecialItemQuantity('wheelchair', currentQty - 1)
                                        }
                                    }}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                >
                                    <MinusIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="w-8 text-center text-sm font-medium">
                                    {selectedAddons.departure.special_items.wheelchair || 0}
                                </span>
                                <button
                                    onClick={() => {
                                        const currentQty = selectedAddons.departure.special_items.wheelchair || 0
                                        if (currentQty < getNonInfantCount()) {
                                            updateSpecialItemQuantity('wheelchair', currentQty + 1)
                                        }
                                    }}
                                    disabled={(selectedAddons.departure.special_items.wheelchair || 0) >= getNonInfantCount()}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <PlusIcon className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="text-sm font-semibold text-gray-900 ml-2">
                                    {addonPriceConfig.special_items.wheelchair_price} BHD
                                </span>
                            </div>
                        </div>

                        {/* Stroller - Only show if there are infants */}
                        {getInfantCount() > 0 && (
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900">{t('Stroller')}</h4>
                                        <p className="text-xs text-gray-500">{t('Max')}: {getTotalPassengers()} {t('items')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => {
                                            const currentQty = selectedAddons.departure.special_items.stroller || 0
                                            if (currentQty > 0) {
                                                updateSpecialItemQuantity('stroller', currentQty - 1)
                                            }
                                        }}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                    >
                                        <MinusIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="w-8 text-center text-sm font-medium">
                                        {selectedAddons.departure.special_items.stroller || 0}
                                    </span>
                                    <button
                                        onClick={() => {
                                            const currentQty = selectedAddons.departure.special_items.stroller || 0
                                            if (currentQty < getTotalPassengers()) {
                                                updateSpecialItemQuantity('stroller', currentQty + 1)
                                            }
                                        }}
                                        disabled={(selectedAddons.departure.special_items.stroller || 0) >= getTotalPassengers()}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <PlusIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <span className="text-sm font-semibold text-gray-900 ml-2">
                                        {addonPriceConfig.special_items.stroller_price} BHD
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Shuttle Service Section - Only show if available for international trips */}
            {isShuttleServiceAvailable() && (
                <div className="mb-6">
                    <button
                        onClick={() => setOpenAddonSection(openAddonSection === 'oneway-shuttle' ? null : 'oneway-shuttle')}
                        className="w-full flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                        <div className="text-left">
                            <h3 className="text-lg font-semibold text-gray-900">{t('Shuttle Service')}</h3>
                            <p className="text-sm text-gray-600">{t('Bus transport from destination port to drop-off points')}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z"/>
                                </svg>
                            </div>
                            <ChevronDownIcon 
                                className={`w-5 h-5 text-gray-500 transform transition-transform ${openAddonSection === 'oneway-shuttle' ? 'rotate-180' : ''}`}
                            />
                        </div>
                    </button>
                    {openAddonSection === 'oneway-shuttle' && (
                        <div className="space-y-3 mt-3">
                            {getShuttleProviders().map((shuttleProvider) => {
                                if (!shuttleProvider.shuttle_provider?.isactive) return null;
                                
                                const provider = shuttleProvider.shuttle_provider;
                                const isSelected = selectedAddons.departure.shuttle_service.selected && 
                                                 selectedAddons.departure.shuttle_service.route_shuttle_id === shuttleProvider.route_shuttle_id;
                                
                                return (
                                    <div key={shuttleProvider.route_shuttle_id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start space-x-3">
                                                <input
                                                    type="checkbox"
                                                    id={`oneway-shuttle-${shuttleProvider.route_shuttle_id}`}
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        updateShuttleServiceSelection(e.target.checked, e.target.checked ? shuttleProvider : null)
                                                    }}
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <label htmlFor={`oneway-shuttle-${shuttleProvider.route_shuttle_id}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                                                        {provider.provider_name}
                                                    </label>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {provider.service_description?.en || provider.service_description?.ar || 'Shuttle service to destination'}
                                                    </p>
                                                    {provider.contact && (
                                                        <div className="text-xs text-gray-500 mt-2">
                                                            <p>{t('Contact')}: {provider.contact.person}</p>
                                                            <p>{t('Phone')}: {provider.contact.mobile_number}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-lg font-semibold text-gray-900">
                                                    {provider.price?.gross || provider.price?.base} BHD
                                                </span>
                                                {provider.price?.tax > 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        {t('Incl. tax')}: {provider.price.tax} BHD
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}